/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import {
  ActivityIndicator,
  Linking,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import MapView, { Marker } from 'react-native-maps';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { invokeEdgeOrPresent } from '../../../shared/supabase/invokeEdgeOrPresent'
import { supabase } from '../../../shared/supabase/supabaseClient';
import { showSuccess, showError } from '../../../shared/ui/appToast';
import { canRenderNativeMapView } from '../../../shared/maps/canRenderNativeMapView';
import { PaperSheet } from '../../../shared/ticket/PaperSheet'
import { TicketButton } from '../../../shared/ticket/TicketButton';

type Props = {
  branchId: string;
  branchName: string;
  branchAddress: string;
  canEdit: boolean;
  onSaved?: (coords: { address: string; latitude: number; longitude: number }) => void;
};

type GeocodeResult = {
  label: string;
  lat: number;
  lng: number;
};

const STREET_DELTA = 0.005;

const DEFAULT_REGION = {
  latitude: -34.6037,
  longitude: -58.3816,
  latitudeDelta: 0.01,
  longitudeDelta: 0.01,
};

export function BranchLocationPicker({
  branchId,
  branchName,
  branchAddress,
  canEdit,
  onSaved,
}: Props) {
  const mapRef = useRef<MapView | null>(null);
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<GeocodeResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [loadFailed, setShowError] = useState(false);

  const focusOn = useCallback((latitude: number, longitude: number) => {
    mapRef.current?.animateToRegion(
      {
        latitude,
        longitude,
        latitudeDelta: STREET_DELTA,
        longitudeDelta: STREET_DELTA,
      },
      300,
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('branches')
          .select('latitude, longitude, address')
          .eq('id', branchId)
          .single();
        if (cancelled) return;
        if (error) {
          setShowError(true);
          return;
        }
        if (data?.latitude && data?.longitude) {
          setLat(data.latitude);
          setLng(data.longitude);
        }
        if (data?.address) {
          setSearchText(data.address);
        }
      } catch {
        if (!cancelled) setShowError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [branchId]);

  const handleSearch = useCallback(async () => {
    if (searchText.trim().length < 4) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data, error, presented, kind } = await invokeEdgeOrPresent('geocode-address', {
        body: { text: searchText.trim() },
      }, {
        module: 'settings',
        op: 'settings.geocode',
        fallbackMessage: 'No se pudo buscar la dirección',
      });
      if (error) {
        if (!presented && kind === 'offline') {
          showError('Sin conexión', 'Necesitás internet para buscar la dirección.');
        }
        setSearchResults([]);
        return;
      }
      const results: GeocodeResult[] = (data as any)?.results ?? [];
      setSearchResults(results);
      setShowResults(true);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [searchText]);

  const handleSelectResult = (result: GeocodeResult) => {
    setLat(result.lat);
    setLng(result.lng);
    setSearchText(result.label);
    setShowResults(false);
    setSearchResults([]);
    focusOn(result.lat, result.lng);
  };

  const handleMapDrag = (coordinate: { latitude: number; longitude: number }) => {
    setLat(coordinate.latitude);
    setLng(coordinate.longitude);
    setSearchText((prev) => prev ? `${prev} (ajustado en el mapa)` : 'Ubicación ajustada en el mapa');
  };

  const handleSave = async () => {
    if (lat === null || lng === null) {
      showError('Falta ubicación', 'Confirmá el punto en el mapa.');
      return;
    }
    setSaving(true);
    try {
      const updatedAt = new Date().toISOString();
      const { error } = await supabase.rpc('apply_branch_settings_command', {
        p_operation: 'update',
        p_payload: {
          branchId,
          operationId: `${branchId}:${updatedAt}`,
          name: branchName,
          address: searchText.trim() || null,
          latitude: lat,
          longitude: lng,
        },
      });
      if (error) throw error;
      showSuccess('Ubicación guardada');
      onSaved?.({
        address: searchText.trim(),
        latitude: lat,
        longitude: lng,
      });
    } catch (err: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: err, title: 'Error', userMessage: 'No se pudo guardar la ubicación.' });
    } finally {
      setSaving(false);
    }
  };

  const initialRegion =
    lat != null && lng != null
      ? {
          latitude: lat,
          longitude: lng,
          latitudeDelta: STREET_DELTA,
          longitudeDelta: STREET_DELTA,
        }
      : DEFAULT_REGION;

  if (loading) {
    return (
      <PaperSheet padded testID="branch.location.loading">
        <Text style={localStyles.cardTitle}>Ubicación del local</Text>
        <ActivityIndicator size="small" color={ticketColors.gold} />
      </PaperSheet>
    );
  }

  if (loadFailed) {
    return (
      <PaperSheet padded testID="branch.location.error">
        <Text style={localStyles.cardTitle}>Ubicación del local</Text>
        <Text style={[localStyles.caption, { color: ticketColors.bad }]}>
          No se pudo cargar la ubicación de la sucursal.
        </Text>
      </PaperSheet>
    );
  }

  return (
    <PaperSheet padded testID="branch.location.picker">
      <View style={localStyles.cardStack}>
        <Text style={localStyles.cardTitle}>Ubicación del local</Text>
        <Text style={localStyles.caption}>
          Buscá tu dirección y ajustala en el mapa si hace falta. Ese punto es el origen de las zonas de delivery.
        </Text>

        <View style={localStyles.searchContainer}>
          <TextInput
            style={[localStyles.input, { flex: 1 }]}
            value={searchText}
            onChangeText={(v) => {
              setSearchText(v);
              setShowResults(false);
            }}
            placeholder="Ej: Av. Cabildo 2000, CABA"
            placeholderTextColor={ticketColors.ink3}
            editable={canEdit && !saving}
            onSubmitEditing={handleSearch}
            returnKeyType="search"
          />
          <View style={localStyles.searchBtnWrap}>
            <TicketButton
              variant="ghost"
              label={searching ? '…' : 'Buscar'}
              onPress={handleSearch}
              disabled={!canEdit || saving || searching}
              testID="branch.location.search"
            />
          </View>
        </View>

        {showResults && searchResults.length > 0 && (
          <View style={localStyles.resultsList}>
            {searchResults.map((r, i) => (
              <TouchableOpacity
                key={i}
                style={localStyles.resultItem}
                onPress={() => handleSelectResult(r)}
              >
                <Text style={localStyles.resultText}>{r.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        <View style={localStyles.mapContainer}>
          {canRenderNativeMapView() ? (
            <MapView
              ref={mapRef}
              style={localStyles.map}
              initialRegion={initialRegion}
              showsUserLocation={false}
            >
              {lat != null && lng != null && (
                <Marker
                  coordinate={{ latitude: lat, longitude: lng }}
                  draggable={canEdit && !saving}
                  onDragEnd={(e) => handleMapDrag(e.nativeEvent.coordinate)}
                />
              )}
            </MapView>
          ) : (
            <View style={[localStyles.map, localStyles.mapFallback]}>
              <Text style={localStyles.mapFallbackTitle}>Mapa no disponible en este build</Text>
              <Text style={localStyles.mapFallbackBody}>
                Buscá la dirección arriba y guardá. Para ver el pin en el mapa hace falta la API key de
                Google Maps en el binario Android.
              </Text>
              {lat != null && lng != null ? (
                <TouchableOpacity
                  onPress={() => void Linking.openURL(`https://www.google.com/maps?q=${lat},${lng}`)}
                  accessibilityRole="button"
                >
                  <Text style={localStyles.mapFallbackLink}>Ver en Google Maps</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          )}
        </View>

        {lat !== null && lng !== null && (
          <Text style={localStyles.coordsText}>
            Lat: {lat.toFixed(6)} • Lng: {lng.toFixed(6)}
          </Text>
        )}

        {canEdit && (
          <TicketButton
            variant="gold"
            label={saving ? 'Guardando…' : 'Guardar ubicación'}
            onPress={handleSave}
            disabled={saving}
            testID="branch.location.save"
          />
        )}
      </View>
    </PaperSheet>
  );
}

const localStyles = StyleSheet.create({
  cardStack: {
    gap: spacing.md,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  caption: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 18,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing.sm,
  },
  searchBtnWrap: {
    justifyContent: 'center',
  },
  input: {
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    fontSize: 15,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  resultsList: {
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
  },
  resultItem: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  resultText: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    color: ticketColors.ink,
  },
  mapContainer: {
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
  },
  map: {
    width: '100%',
    height: 240,
  },
  mapFallback: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    gap: spacing.sm,
    backgroundColor: ticketColors.paper2,
  },
  mapFallbackTitle: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
    textAlign: 'center',
  },
  mapFallbackBody: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.ink2,
    lineHeight: 18,
    textAlign: 'center',
  },
  mapFallbackLink: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.gold,
  },
  coordsText: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink2,
    textAlign: 'center',
  },
});
